/**
 * Vue plugin to manage authentication with Doggo and Cognito.
 * @module Doggo
 */

import Auth from "@aws-amplify/auth"
import * as Vue from "vue"

/**
 * This namespace is accessible on the Vue prototype after installation.
 * @namespace $auth
 * @example
 * // Accessible in a component
 * async beforeMount() {
 *     await this.$auth.login()
 * },
 */
const $auth = {
  /**
   * AWS Amplify API from {@link https://aws-amplify.github.io/amplify-js/api/classes/authclass.html|@aws-amplify/auth}
   */
  amplifyAuth: Auth,

  /**
   * Whether a user is logged in.
   * @type {boolean}
   */
  authenticated: false,

  /**
   * Whether a user is authorized to use the app, ie has the `user` role.
   * @type {boolean}
   */
  authorized: false,

  /**
   * The current logged in user.
   * @type {object}
   */
  user: null,

  /**
   * The ID for the session refresh setInterval.
   * @type {number}
   */
  refreshInterval: null,

  /**
   * Returns the current Cognito session and refreshes authenticated status.
   * @returns {Promise<object|null>} session
   * @private
   */
  getSession() {
    return Auth.currentSession()
      .then((session) => {
        this.authenticated = true
        return session
      })
      .catch(() => {
        this.authenticated = false
        return null
      })
  },

  /**
   * Returns the Cognito Identity JWT, used for authorization.
   * @returns {Promise<string>} JWT - base64-encoded JSON Web Token
   * @private
   */
  async jwt() {
    const session = await this.getSession()
    if (session) {
      return session.getIdToken().getJwtToken()
    } else {
      return ""
    }
  },

  /**
   * Get the claims of the logged in user.
   * @returns {Promise<object|null>} user
   * @private
   */
  async getUser() {
    const session = await this.getSession()
    if (session) {
      const user = session.getIdToken().payload
      user.roles = JSON.parse(user.roles)
      return user
    } else {
      return null
    }
  },

  /**
   * Login user via Shibboleth.
   * @returns {Promise<object>} user
   */
  async login() {
    try {
      await Auth.currentAuthenticatedUser()
    } catch (error) {
      // No user? Login via Shibboleth.
      console.log("Redirecting to WebAuth...")
      Auth.federatedSignIn({ provider: "ua-shibboleth" })
      return new Promise((r) => setTimeout(r, 5000)) // Block execution until redirect
    } finally {
      this.user = await this.getUser()
      this.authenticated = true
      this.authorized = this.hasRole("user")

      // Refresh tokens every 30min
      this.refreshInterval = setInterval(
        () => Auth.currentSession,
        30 * 60 * 1000,
      )
    }
  },

  /**
   * Destroy Cognito tokens and logout from Shibboleth.
   * Redirects to WebAuth log out page.
   */
  async logout(options = { global: false }) {
    await Auth.signOut(options)
  },

  /**
   * Checks whether the user has a certain role.
   * @param {string} role
   * @returns {boolean} boolean
   */
  hasRole(role) {
    const clientId = Auth.userPool.clientId
    const user = this.user
    const appRoles = user && user.roles && user.roles[clientId]
    return Boolean(appRoles && appRoles.includes(role))
  },
}

/**
 * Axios interceptor which adds identity JWT `Authorization` header to requests.
 * @returns {object} config - axios request config
 * @example
 * const api = axios.create()
 * api.interceptors.request.use(authHeaderInterceptor)
 */
export async function authHeaderInterceptor(config) {
  const jwt = await $auth.jwt()
  if (jwt) {
    config.headers.common["Authorization"] = `Bearer ${jwt}`
  }
  return config
}

/** Returns a rudimentary testing mock of the auth object. */
function getStub() {
  const stub = {
    authenticated: true,
    authorized: true,
    user: {},
    login: async () => {},
    logout: async () => {},
    hasRole: () => true,
    jwt: async () => "jwt",
  }
  const handler = {
    get: function (target, prop, _receiver) {
      if (Reflect.has(target, prop)) return Reflect.get(...arguments)
      return async () => null
    },
  }
  return new Proxy(stub, handler)
}

/**
 * @param {Vue} app - Vue instance
 * @param {boolean} [disable=false] - Mocks the auth object for testing.
 * @param {Object} amplify - Options to configure the underlying Amplify Auth instance.
 * @param {string} amplify.region - AWS Region of User Pool
 * @param {string} amplify.userPoolWebClientId - Cognito app client ID
 * @param {string} amplify.userPoolId - Cognito User Pool ID
 * @param {string} amplify.domain - Cognito Domain
 * @param {string} amplify.redirectSignIn - URL to redirect to after sign in. Must match client config exactly!
 */
async function install(app, options) {
  const amplifyConfig = {
    region: options.amplify.region,
    userPoolWebClientId: options.amplify.userPoolWebClientId,
    userPoolId: options.amplify.userPoolId,
    mandatorySignIn: true,
    oauth: {
      domain: options.amplify.domain,
      scope: ["openid", "profile", "email", "phone"],
      redirectSignIn: options.amplify.redirectSignIn,
      redirectSignOut: "https://shibboleth.arizona.edu/webauth/logout",
      responseType: "code",
    },
  }
  Auth.configure(amplifyConfig)

  let auth = options.disable ? getStub() : $auth
  const version = Number(app.version.split(".")[0])
  if (version === 3) {
    app.config.globalProperties.$auth = Vue.reactive(auth)
  } else if (version === 2) {
    app.prototype.$auth = app.observable(auth)
  }
}

export default { install }
